import React, { useEffect, useState, useRef } from 'react';
import api from '../api/axios';
import { filterPhone, isValidEmail } from '../utils/inputValidation';
import { useToast } from '../components/Toast';
import { io } from 'socket.io-client';
import {
  Users, UserIcon, Calendar, TrendingUp, Scissors, Clock, Smartphone,
  Plus, LogOut, Store, X, Trash2, Edit, Save, AlertCircle,
  ShoppingBag, ChevronLeft, ChevronRight, Menu, History, ReceiptText,
  CalendarDays, Building2, Bell, Star
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import EmployeeProfileSettings from '../components/EmployeeProfileSettings';
import AdminPlanning from '../components/AdminPlanning';
import AdminTransactions from '../components/AdminTransactions';
import TransactionManager from '../components/TransactionManager';
import AdminFinances from '../components/AdminFinances';
import AuditLogs from '../components/AuditLogs';
import NotificationBell from '../components/NotificationBell';
import AdminCategories from '../components/AdminCategories';
import AdminSalonSettings from '../components/AdminSalonSettings';
import BookingAssignModal from '../components/BookingAssignModal';
import EmployeeLeaveModal from '../components/EmployeeLeaveModal';
import AdminShop from '../components/AdminShop';
import AdminPointage from '../components/AdminPointage';
import AdminClients from './AdminClients';
import AdminAvis from '../components/AdminAvis';
import AvisQRModal from '../components/AvisQRModal';
import AdminSubscriptions from '../components/AdminSubscriptions';


const displayCurrency = (devise) => {
  switch (devise) {
    case 'XOF': return 'FCFA';
    case 'EUR': return '€';
    case 'USD': return '$';
    default: return devise;
  }
};

const NAV_ITEMS = [
  { id: 'planning',     icon: <Calendar size={20}/>,    label: 'Planning'   },
  { id: 'caisse',       icon: <ShoppingBag size={20}/>, label: 'Caisse'     },
  { id: 'finances',     icon: <TrendingUp size={20}/>,  label: 'Finances'   },
  { id: 'transactions', icon: <ReceiptText size={20}/>, label: 'Historique' },
  { id: 'audit',        icon: <History size={20}/>,     label: 'Journal'    },
  { id: 'categories',   icon: <Scissors size={20}/>,    label: 'Catégories' },
  { id: 'pointage',     icon: <Clock size={20}/>,       label: 'Pointage'   },
  { id: 'clients',      icon: <Bell size={20}/>,        label: 'Clients'    },
  { id: 'abonnements',  icon: <CalendarDays size={20}/>, label: 'Abonnements'},
  { id: 'boutique',     icon: <Store size={20}/>,       label: 'Boutique'   },
  { id: 'equipe',       icon: <Users size={20}/>,       label: 'Équipe'     },
  { id: 'avis',         icon: <Star size={20}/>,         label: 'Avis'       },
  { id: 'salon',        icon: <Building2 size={20}/>,   label: 'Mon salon'  },
  { id: 'profil',       icon: <UserIcon size={20}/>,    label: 'Profil'     },
];

const AdminSalonDashboard = () => {
  const showToast = useToast();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Evite deux instances de NotificationBell (une seule montée selon le breakpoint)
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const [activeTab, setActiveTab] = useState(localStorage.getItem('adminActiveTab') || 'caisse');

  useEffect(() => {
    localStorage.setItem('adminActiveTab', activeTab);
  }, [activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setIsMobileMenuOpen(false);
  };

  // Écoute les actions bloquées par le backend quand le salon est suspendu
  useEffect(() => {
    const handler = (e) => setSuspendedMsg(e.detail?.message || 'Ce salon est suspendu. Veuillez contacter l\'administrateur de l\'outil pour le réactiver.');
    window.addEventListener('salon-suspended', handler);
    return () => window.removeEventListener('salon-suspended', handler);
  }, []);

  const [salon, setSalon] = useState(null);
  const [adminInfo, setAdminInfo] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [scheduleErrorMsg, setScheduleErrorMsg] = useState('');
  const [stats, setStats] = useState({ caDuJour: 0, depensesDuJour: 0, profitNet: 0 });
  const [prefillData, setPrefillData] = useState(null);
  const [liveNotifs, setLiveNotifs]   = useState([]);
  const socketRef = useRef(null);
  const [pendingAvis,  setPendingAvis]  = useState(null);    // { bookingId, clientNom }
  const [showAvisQR,   setShowAvisQR]   = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [bookingToAssign, setBookingToAssign] = useState(null);
  const [leaveEmployee, setLeaveEmployee] = useState(null);
  const [suspendedMsg, setSuspendedMsg] = useState('');
  const [employeeData, setEmployeeData] = useState({
    nom: '', prenom: '', email: '', password: '', telephone: '', specialite: 'Coiffure'
  });

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const results = await Promise.allSettled([
        api.get('/salon/me', { headers }),
        api.get('/salon/employees', { headers }),
        api.get('/admin/me', { headers }),
        api.get('/admin/stats/daily', { headers })
      ]);
      if (results[0].status === 'fulfilled') setSalon(results[0].value.data.data);
      if (results[1].status === 'fulfilled') setEmployees(results[1].value.data.data);
      if (results[2].status === 'fulfilled') setAdminInfo(results[2].value.data.data);
      if (results[3].status === 'fulfilled') setStats(results[3].value.data.data);
    } catch (err) {
      showToast("Erreur de chargement des données", "error");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [navigate]);

  // Connexion Socket.io pour les notifications temps réel
  useEffect(() => {
    if (!salon?._id) return;
    const SOCKET_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;
    socket.emit('join-salon', salon._id);

    socket.on('booking:new', (data) => {
      const msg = `Nouveau RDV : ${data.clientPrenom} ${data.clientNom} le ${data.date} à ${data.heure}`;
      showToast(msg, 'info');
      setLiveNotifs(prev => [{ id: Date.now(), type: 'booking', msg, raw: data }, ...prev].slice(0, 20));
    });

    socket.on('order:new', (data) => {
      const msg = `Nouvelle commande : ${data.clientPrenom} ${data.clientNom} — ${data.total?.toLocaleString('fr-FR')} FCFA`;
      showToast(msg, 'info');
      setLiveNotifs(prev => [{ id: Date.now(), type: 'order', msg, raw: data }, ...prev].slice(0, 20));
    });

    return () => { socket.disconnect(); };
  }, [salon?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('adminActiveTab');
    navigate('/');
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!isValidEmail(employeeData.email)) {
      setErrorMsg('Adresse email invalide. Vérifiez le format (ex : nom@domaine.com).'); return;
    }
    try {
      const token = localStorage.getItem('token');
      await api.post('/admin/employees', employeeData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEmployeeModal(false);
      setEmployeeData({ nom: '', prenom: '', email: '', password: '', telephone: '', specialite: 'Coiffure' });
      showToast("Employé ajouté avec succès !");
      fetchData();
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "Erreur lors de l'ajout.");
    }
  };

  const handleArchiveEmployee = async (employeeId, name) => {
    if (window.confirm(`Voulez-vous vraiment archiver ${name} ?`)) {
      try {
        const token = localStorage.getItem('token');
        await api.delete(`/admin/employees/${employeeId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast(`${name} a été archivé.`);
        fetchData();
      } catch (err) {
        showToast("Erreur lors de l'archivage", "error");
      }
    }
  };

  const openScheduleModal = (emp) => {
    setSelectedEmployee({ ...emp });
    setScheduleErrorMsg('');
    setShowScheduleModal(true);
  };

  const updateDayTime = (dayNum, field, value) => {
    let digits = value.replace(/\D/g, '');
    digits = digits.slice(0, 4);
    let formatted = digits.length <= 2 ? digits : digits.slice(0, 2) + 'h' + digits.slice(2);
    const updatedHoraires = [...selectedEmployee.horaires];
    const dayIndex = updatedHoraires.findIndex(h => h.jour === dayNum);
    if (dayIndex !== -1) {
      updatedHoraires[dayIndex] = { ...updatedHoraires[dayIndex], [field]: formatted };
    }
    setSelectedEmployee({ ...selectedEmployee, horaires: updatedHoraires });
  };

  const handleSaveSchedule = async (e) => {
    e.preventDefault();
    setScheduleErrorMsg('');
    const timeRegex = /^[0-9]{2}h[0-9]{2}$/;
    const timeVal = (t) => {
      if (!t) return -1;
      const [h, m] = t.split('h').map(Number);
      return h * 60 + (m || 0);
    };
    const isValid = selectedEmployee.horaires.every(h => {
      // Jour de repos : debut === fin ou vide → OK
      if (!h.debut || !h.fin || h.debut === h.fin) return true;
      if (!timeRegex.test(h.debut) || !timeRegex.test(h.fin)) return false;
      const [hDeb, mDeb] = h.debut.split('h').map(Number);
      const [hFin, mFin] = h.fin.split('h').map(Number);
      if (hDeb > 23 || mDeb > 59 || hFin > 23 || mFin > 59) return false;
      if (timeVal(h.debut) >= timeVal(h.fin)) return false; // début doit être AVANT fin
      return true;
    });
    if (!isValid) {
      setScheduleErrorMsg("Horaires invalides : vérifiez le format (09h00) et que l'heure de début est bien avant l'heure de fin.");
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await api.put(`/admin/employees/${selectedEmployee._id}`,
        { horaires: selectedEmployee.horaires },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowScheduleModal(false);
      showToast("Horaires mis à jour !");
      fetchData();
    } catch (err) {
      setScheduleErrorMsg(err.response?.data?.message || "Erreur lors de la sauvegarde.");
    }
  };

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-slate-50">
      <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex font-sans overflow-x-hidden">

      {/* ── TOPBAR MOBILE - hamburger unique ── */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-100 z-40 flex items-center justify-between px-4 shadow-sm">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white">
            <Scissors size={16} />
          </div>
          <span className="font-black text-slate-800 tracking-tight text-sm">SamaSalon</span>
        </div>
        {salon?.nom && (
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest truncate max-w-[120px]">
            {salon.nom}
          </span>
        )}
        <div className="flex items-center gap-1">
          {/* Bell + badge live (mobile/tablette) */}
          {!isDesktop && (
            <div className="relative">
              <NotificationBell salonId={salon?._id} />
              {liveNotifs.length > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 rounded-full bg-red-500 text-white text-[9px] font-black flex items-center justify-center pointer-events-none">
                  {liveNotifs.length > 9 ? '9+' : liveNotifs.length}
                </span>
              )}
            </div>
          )}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 text-slate-600 hover:bg-slate-50 rounded-xl transition-colors"
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>


      {/* ── CLOCHE DESKTOP : fixée en haut à droite ── */}
      {isDesktop && (
        <div className="fixed top-4 right-4 z-[60]">
          <div className="relative">
            <NotificationBell salonId={salon?._id} />
            {liveNotifs.length > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 rounded-full bg-red-500 text-white text-[9px] font-black flex items-center justify-center pointer-events-none">
                {liveNotifs.length > 9 ? '9+' : liveNotifs.length}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── OVERLAY ── */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* ── SIDEBAR desktop + DRAWER mobile ── */}
      <aside className={`
        fixed h-full z-50 bg-white border-r border-slate-100 flex flex-col
        transition-all duration-300 ease-in-out shadow-xl lg:shadow-none
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${isCollapsed ? 'lg:w-20' : 'lg:w-72'} w-72
      `}>
        {/* Toggle collapse desktop */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hidden lg:flex absolute -right-3 top-10 bg-white border border-slate-200 rounded-full p-1.5 text-slate-400 hover:text-blue-600 shadow-sm z-30 transition-transform hover:scale-110"
        >
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>

        <div className={`flex flex-col h-full ${isCollapsed ? 'lg:px-3 px-6' : 'px-6'} py-8`}>

          {/* Logo desktop */}
          <div className="hidden lg:flex items-center space-x-3 mb-10 overflow-hidden">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-lg shrink-0">
              <Scissors size={20} />
            </div>
            {!isCollapsed && (
              <span className="text-xl font-black text-slate-800 whitespace-nowrap animate-in fade-in">
                SamaSalon
              </span>
            )}
          </div>

          {/* Infos salon - drawer mobile uniquement */}
          <div className="lg:hidden mb-8 mt-4 pb-6 border-b border-slate-100">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Établissement</p>
            <p className="font-black text-slate-800 text-lg">{salon?.nom}</p>
          </div>

          {/* Nav - tous les items dans le même menu */}
          <nav className="space-y-1 w-full flex-1 overflow-y-auto min-h-0">
            {NAV_ITEMS.map(item => (
              <NavItem
                key={item.id}
                icon={item.icon}
                label={item.label}
                active={activeTab === item.id}
                collapsed={isCollapsed}
                onClick={() => handleTabChange(item.id)}
              />
            ))}
          </nav>

          {/* Borne de pointage + Déconnexion */}
          <div className="pt-4 border-t border-slate-100 mt-4 space-y-1">
            <a
              href="/kiosk"
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-3 text-amber-600 font-bold hover:bg-amber-50 w-full p-3 rounded-xl transition-all
                ${isCollapsed ? 'lg:justify-center' : ''}`}
              title="Borne de pointage"
            >
              <Smartphone size={20} className="shrink-0" />
              <span className={isCollapsed ? 'lg:hidden' : ''}>Borne de pointage</span>
            </a>

            <button
              onClick={handleLogout}
              className={`flex items-center gap-3 text-red-500 font-bold hover:bg-red-50 w-full p-3 rounded-xl transition-all
                ${isCollapsed ? 'lg:justify-center' : ''}`}
            >
              <LogOut size={20} className="shrink-0" />
              <span className={isCollapsed ? 'lg:hidden' : ''}>Déconnexion</span>
            </button>
          </div>
        </div>
      </aside>

      {/* ── CONTENU PRINCIPAL ── */}
      <main className={`
        flex-1 min-w-0 overflow-x-hidden transition-all duration-300 ease-in-out
        ${isCollapsed ? 'lg:ml-20' : 'lg:ml-72'}
        px-4 py-4 md:px-8 md:py-8 lg:px-12 lg:py-12
        mt-14 lg:mt-0
      `}>

        {/* ── BANNIÈRE SALON SUSPENDU ─────────────────────────────────── */}
        {(salon?.isActive === false || suspendedMsg) && (
          <div className="mb-6 rounded-2xl overflow-hidden shadow-lg border border-amber-200">
            {/* Barre de couleur en haut */}
            <div className="h-1.5 bg-gradient-to-r from-amber-400 to-red-400" />
            <div className="bg-amber-50 px-5 py-4 flex items-start gap-4">
              {/* Icône */}
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                  <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clipRule="evenodd" />
                </svg>
              </div>
              {/* Texte */}
              <div className="flex-1 min-w-0">
                <p className="font-black text-amber-900 text-sm tracking-tight">
                  Ce salon est suspendu
                </p>
                <p className="text-amber-700 text-xs mt-0.5 leading-relaxed">
                  {suspendedMsg || 'Ce salon a été désactivé. Veuillez contacter l\'administrateur de l\'outil pour le réactiver.'}
                </p>
              </div>
              {/* Badge statut */}
              <span className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-[11px] font-black uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                Inactif
              </span>
            </div>
          </div>
        )}

        <div className="max-w-[1400px] mx-auto">

          {/* ── ONGLET ÉQUIPE ── */}
          {activeTab === 'equipe' && (
            <div className="animate-in fade-in duration-500 space-y-6">

              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl md:text-2xl font-black text-slate-800">Équipe</h2>
                  <p className="text-sm text-slate-400 font-medium mt-0.5">
                    {employees.length} membre{employees.length !== 1 ? 's' : ''} actif{employees.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  onClick={() => setShowEmployeeModal(true)}
                  className="flex items-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-2xl font-black text-sm shadow-lg shadow-blue-100 hover:bg-blue-700 transition-colors"
                >
                  <Plus size={15} /> Ajouter un membre
                </button>
              </div>

              {/* Stats strip */}
              <div className="grid grid-cols-3 gap-3">
                <StatCard color="emerald" icon={<TrendingUp />} label="CA du jour"
                  value={`${stats.caDuJour?.toLocaleString() || 0} ${displayCurrency(salon?.devise)}`} />
                <StatCard color="purple" icon={<Users />} label="Staff actif" value={employees.length} />
                <StatCard color="blue"   icon={<Calendar />} label="RDV du jour" value={stats.rdvDuJour || 0} />
              </div>

              {/* Grille employés */}
              {employees.length === 0 ? (
                <div className="bg-white rounded-3xl border border-slate-100 shadow-sm py-20 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                    <Users size={22} className="text-slate-300" />
                  </div>
                  <p className="text-slate-500 font-bold text-sm">Aucun membre pour le moment</p>
                  <p className="text-slate-300 text-xs mt-1">Ajoutez votre premier collaborateur.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {employees.map((emp, idx) => {
                    const PALETTE = ['#3B82F6','#8B5CF6','#10B981','#F59E0B','#F43F5E','#14B8A6','#F97316','#64748B'];
                    const color   = PALETTE[idx % PALETTE.length];
                    const initials = `${emp.prenom?.[0] || ''}${emp.nom?.[0] || ''}`.toUpperCase();
                    const r = parseInt(color.slice(1,3),16), g = parseInt(color.slice(3,5),16), b = parseInt(color.slice(5,7),16);
                    const alphaBg  = `rgba(${r},${g},${b},0.10)`;
                    const alphaBd  = `rgba(${r},${g},${b},0.20)`;

                    return (
                      <div key={emp._id} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col group hover:shadow-md transition-shadow">
                        {/* Barre couleur */}
                        <div style={{ height: '3px', background: `linear-gradient(90deg, ${color}, ${alphaBg})` }} />

                        <div className="p-5 flex flex-col gap-4 flex-1">
                          {/* Avatar + nom */}
                          <div className="flex items-center gap-3">
                            <div
                              className="w-12 h-12 rounded-2xl flex items-center justify-center font-black text-base shrink-0"
                              style={{ background: alphaBg, border: `1.5px solid ${alphaBd}`, color }}
                            >
                              {initials}
                            </div>
                            <div className="min-w-0">
                              <p className="font-black text-slate-800 text-sm leading-tight">
                                {emp.prenom} {emp.nom}
                              </p>
                              {emp.specialite && (
                                <span
                                  className="inline-block mt-1 px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wide"
                                  style={{ background: alphaBg, color }}
                                >
                                  {emp.specialite}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Contact */}
                          <div className="space-y-1.5 flex-1">
                            {emp.userId?.telephone && (
                              <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
                                <div className="w-6 h-6 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">
                                  <Smartphone size={11} className="text-slate-400" />
                                </div>
                                {emp.userId.telephone}
                              </div>
                            )}
                            {emp.userId?.email && (
                              <div className="flex items-center gap-2 text-xs text-slate-400 font-medium min-w-0">
                                <div className="w-6 h-6 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">
                                  <UserIcon size={11} className="text-slate-400" />
                                </div>
                                <span className="truncate">{emp.userId.email}</span>
                              </div>
                            )}
                            {!emp.userId?.telephone && !emp.userId?.email && (
                              <p className="text-xs text-slate-300 italic">Aucun contact renseigné</p>
                            )}
                          </div>

                          {/* Séparateur */}
                          <div className="h-px bg-slate-50" />

                          {/* Actions */}
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setLeaveEmployee(emp)}
                              title="Gérer les congés"
                              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-[10px] font-black text-slate-400 hover:text-orange-500 hover:bg-orange-50 transition-colors"
                            >
                              <CalendarDays size={13} /> Congés
                            </button>
                            <button
                              onClick={() => openScheduleModal(emp)}
                              title="Modifier les horaires"
                              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-[10px] font-black text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
                            >
                              <Edit size={13} /> Horaires
                            </button>
                            <button
                              onClick={() => handleArchiveEmployee(emp._id, emp.prenom)}
                              title="Archiver"
                              className="w-8 h-8 flex items-center justify-center rounded-xl text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── AUTRES ONGLETS ── */}
          <div className="animate-in fade-in duration-500">
            {activeTab === 'planning' && (
              <AdminPlanning
                salonId={salon?._id}
                employees={employees}
                salonDevise={salon?.devise}
                onFinishBooking={(data) => {
                  const { bookingId, ...caisse } = data;
                  setPrefillData(caisse);
                  if (bookingId) setPendingAvis({ bookingId, clientNom: data.clientNom || 'Client' });
                  setActiveTab('caisse');
                }}
                onAssignBooking={(booking) => { setBookingToAssign(booking); setShowAssignModal(true); }}
              />
            )}
            {activeTab === 'caisse' && (
              <TransactionManager
                employees={employees}
                salonId={salon?._id}
                salonDevise={salon?.devise}
                prefillData={prefillData}
                onTransactionSuccess={() => {
                  fetchData();
                  setPrefillData(null);
                  if (pendingAvis) setShowAvisQR(true);
                }}
              />
            )}
            {activeTab === 'transactions' && (
              <AdminTransactions salonDevise={displayCurrency(salon?.devise)} salonTimezone={salon?.timezone} employees={employees} rawDevise={salon?.devise} salon={salon} />
            )}
            {activeTab === 'finances' && (
              <AdminFinances salonDevise={salon?.devise} salonNom={salon?.nom} salonId={salon?._id} donneesCourbe={stats.historiqueCA} />
            )}
            {activeTab === 'audit' && <AuditLogs salonDevise={salon?.devise} salonTimezone={salon?.timezone} />}
            {activeTab === 'categories' && <AdminCategories salonId={salon?._id} salonDevise={salon?.devise} />}
            {activeTab === 'pointage' && (
              <AdminPointage employees={employees} salonId={salon?._id} />
            )}
            {activeTab === 'clients' && (
              <AdminClients />
            )}
            {activeTab === 'abonnements' && (
              <AdminSubscriptions salonDevise={salon?.devise} employees={employees} />
            )}
            {activeTab === 'boutique' && (
              <AdminShop
                salon={salon}
                onShopSlugGenerated={fetchData}
              />
            )}
            {activeTab === 'avis' && <AdminAvis />}
            {activeTab === 'salon' && (
              <AdminSalonSettings
                salon={salon}
                onSaved={(updated) => setSalon(updated)}
              />
            )}
            {activeTab === 'profil' && (
              <div className="space-y-6">
                <EmployeeProfileSettings userInitialData={adminInfo} />
                <BookingLinkSection salon={salon} onSlugGenerated={fetchData} />
              </div>
            )}
          </div>

        </div>
      </main>

      {/* ── MODAL HORAIRES ── */}
      {showScheduleModal && selectedEmployee && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-t-[2.5rem] sm:rounded-[2.5rem] w-full max-w-xl shadow-2xl relative max-h-[92vh] flex flex-col">
            <div className="p-6 md:p-8 pb-0">
              <button onClick={() => setShowScheduleModal(false)}
                className="absolute top-5 right-5 text-slate-300 hover:text-slate-600 transition-colors p-1">
                <X size={22} />
              </button>
              <h2 className="text-xl font-black text-slate-900">Horaires : {selectedEmployee.prenom}</h2>
              <p className="text-slate-400 mt-1 text-sm font-medium">Format attendu : 09h00</p>
              {scheduleErrorMsg && (
                <div className="mt-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs font-bold flex items-center">
                  <AlertCircle size={16} className="mr-2 shrink-0" />{scheduleErrorMsg}
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto px-6 md:px-8 py-4 space-y-2">
              {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'].map((label, idx) => {
                const dayNum  = idx === 6 ? 0 : idx + 1;
                const dayData = selectedEmployee.horaires.find(h => h.jour === dayNum);
                const isRest  = !dayData?.debut || !dayData?.fin || dayData.debut === dayData.fin;

                const toggleRest = () => {
                  const updatedHoraires = [...selectedEmployee.horaires];
                  const dayIndex = updatedHoraires.findIndex(h => h.jour === dayNum);
                  if (dayIndex !== -1) {
                    updatedHoraires[dayIndex] = isRest
                      ? { ...updatedHoraires[dayIndex], debut: '09h00', fin: '18h00' }
                      : { ...updatedHoraires[dayIndex], debut: '00h00', fin: '00h00' };
                  }
                  setSelectedEmployee({ ...selectedEmployee, horaires: updatedHoraires });
                };

                return (
                  <div key={label} className={`flex items-center justify-between p-3.5 rounded-2xl border transition-colors ${
                    isRest ? 'bg-slate-100/60 border-slate-100' : 'bg-slate-50 border-slate-100'
                  }`}>
                    {/* Nom du jour + toggle repos */}
                    <button
                      type="button"
                      onClick={toggleRest}
                      className={`flex items-center gap-2 group`}
                      title={isRest ? 'Cliquer pour marquer comme travaillé' : 'Cliquer pour marquer repos'}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                        isRest
                          ? 'border-slate-300 bg-white'
                          : 'border-blue-500 bg-blue-500'
                      }`}>
                        {!isRest && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                      </div>
                      <span className={`font-bold text-sm w-20 text-left transition-colors ${
                        isRest ? 'text-slate-300' : 'text-slate-700'
                      }`}>{label}</span>
                    </button>

                    {/* Champs horaires ou badge repos */}
                    {isRest ? (
                      <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Repos</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <input type="text" maxLength={5} value={dayData?.debut || ''} placeholder="09h00"
                          className="w-16 p-2 text-center bg-white border border-slate-200 rounded-xl text-xs font-black outline-none focus:border-blue-400"
                          onChange={(e) => updateDayTime(dayNum, 'debut', e.target.value)} />
                        <span className="text-slate-300 font-bold text-[10px]">→</span>
                        <input type="text" maxLength={5} value={dayData?.fin || ''} placeholder="18h00"
                          className="w-16 p-2 text-center bg-white border border-slate-200 rounded-xl text-xs font-black outline-none focus:border-blue-400"
                          onChange={(e) => updateDayTime(dayNum, 'fin', e.target.value)} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="p-6 md:p-8 pt-4 border-t border-slate-50">
              <button onClick={handleSaveSchedule}
                className="w-full p-4 bg-slate-900 text-white rounded-2xl font-black flex items-center justify-center hover:bg-black transition-all active:scale-[0.98]">
                <Save size={20} className="mr-2" /> Appliquer les modifications
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL ASSIGN BOOKING ── */}
      {showAssignModal && bookingToAssign && (
        <BookingAssignModal
          booking={bookingToAssign}
          employees={employees}
          onClose={() => { setShowAssignModal(false); setBookingToAssign(null); }}
          onAssigned={() => { fetchData(); setShowAssignModal(false); setBookingToAssign(null); }}
        />
      )}

      {/* ── MODAL CONGÉS EMPLOYÉ ── */}
      {leaveEmployee && (
        <EmployeeLeaveModal
          employee={leaveEmployee}
          onClose={() => setLeaveEmployee(null)}
        />
      )}

      {/* ── MODAL AJOUT EMPLOYÉ ── */}
      {showEmployeeModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-t-[2.5rem] sm:rounded-[2.5rem] w-full max-w-md shadow-2xl relative max-h-[92vh] flex flex-col">
            <div className="p-6 md:p-8 pb-0">
              <button onClick={() => { setShowEmployeeModal(false); setErrorMsg(''); }}
                className="absolute top-5 right-5 text-slate-300 hover:text-slate-600 p-1">
                <X size={22} />
              </button>
              <h2 className="text-xl font-black text-slate-900">Nouvelle Recrue</h2>
              <p className="text-slate-400 text-sm font-medium mt-1">Créez les accès pour votre nouvel employé.</p>
              {errorMsg && (
                <div className="mt-4 p-3 bg-red-50 text-red-700 text-xs font-bold rounded-xl flex items-center">
                  <AlertCircle size={16} className="mr-2 shrink-0" />{errorMsg}
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto px-6 md:px-8 py-4">
              <form id="addEmployeeForm" onSubmit={handleAddEmployee} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase px-1">Prénom</label>
                    <input required
                      className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none focus:ring-2 ring-blue-500/10 font-bold text-slate-700 text-sm"
                      onChange={e => setEmployeeData({ ...employeeData, prenom: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase px-1">Nom</label>
                    <input required
                      className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none focus:ring-2 ring-blue-500/10 font-bold text-slate-700 text-sm"
                      onChange={e => setEmployeeData({ ...employeeData, nom: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase px-1">Email</label>
                  <input type="email" required
                    className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none focus:ring-2 ring-blue-500/10 font-bold text-slate-700 text-sm"
                    onChange={e => setEmployeeData({ ...employeeData, email: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase px-1">Téléphone</label>
                  <input type="tel" required
                    className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none focus:ring-2 ring-blue-500/10 font-bold text-slate-700 text-sm"
                    onChange={e => setEmployeeData({ ...employeeData, telephone: filterPhone(e.target.value) })} />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase px-1">Mot de passe temporaire</label>
                  <input type="password" required
                    className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none focus:ring-2 ring-blue-500/10 font-bold text-slate-700 text-sm"
                    onChange={e => setEmployeeData({ ...employeeData, password: e.target.value })} />
                </div>
              </form>
            </div>
            <div className="p-6 md:p-8 pt-4 border-t border-slate-50">
              <button type="submit" form="addEmployeeForm"
                className="w-full p-4 bg-blue-600 text-white rounded-2xl font-black shadow-lg shadow-blue-100 hover:bg-blue-700 transition-all active:scale-[0.98]">
                Finaliser l'inscription
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL QR AVIS ── */}
      {showAvisQR && pendingAvis && (
        <AvisQRModal
          bookingId={pendingAvis.bookingId}
          clientNom={pendingAvis.clientNom}
          onClose={() => { setShowAvisQR(false); setPendingAvis(null); }}
        />
      )}

    </div>
  );
};

// ── COMPOSANTS INTERNES ──

const NavItem = ({ icon, label, active = false, collapsed = false, onClick }) => (
  <div
    onClick={onClick}
    className={`
      flex items-center p-3.5 rounded-2xl cursor-pointer transition-all relative group
      ${active ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'text-slate-400 hover:bg-slate-50 hover:text-slate-700'}
      ${collapsed ? 'lg:justify-center' : 'space-x-3'}
    `}
  >
    <div className="shrink-0">{icon}</div>
    {!collapsed && <span className="font-bold whitespace-nowrap text-sm">{label}</span>}
    {collapsed && (
      <div className="absolute left-full ml-3 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity font-bold z-50 whitespace-nowrap">
        {label}
      </div>
    )}
  </div>
);

const StatCard = ({ color, icon, label, value }) => {
  const colors = {
    emerald: "bg-emerald-50 text-emerald-600",
    blue: "bg-blue-50 text-blue-600",
    purple: "bg-purple-50 text-purple-600"
  };
  return (
    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm">
      <div className={`w-11 h-11 ${colors[color]} rounded-2xl flex items-center justify-center mb-4`}>
        {React.cloneElement(icon, { size: 22 })}
      </div>
      <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">{label}</p>
      <h3 className="text-2xl font-black text-slate-900 mt-1">{value}</h3>
    </div>
  );
};

// ── Sous-composant : lien de réservation ─────────────────────────────────────
function BookingLinkSection({ salon, onSlugGenerated }) {
  const [generating, setGenerating] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const landingUrl = salon?.bookingSlug
    ? `${window.location.origin}/s/${salon.bookingSlug}`
    : null;

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { default: api } = await import('../api/axios');
      await api.post('/salon/me/generate-slug', {});
      onSlugGenerated();
    } catch {
      // silently ignore
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (!landingUrl) return;
    navigator.clipboard.writeText(landingUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`p-4 rounded-2xl border ${landingUrl ? 'bg-blue-50 border-blue-100' : 'bg-slate-50 border-slate-100'}`}>
      <p className="text-xs font-black text-blue-400 uppercase tracking-widest mb-1">
        Lien public du salon
      </p>
      <p className="text-[11px] text-blue-300 mb-3">
        Réservation &amp; boutique - un seul lien à partager
      </p>

      {landingUrl ? (
        <>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={landingUrl}
              className="flex-1 text-sm font-bold text-slate-700 bg-white border border-slate-100 rounded-xl px-3 py-2 outline-none truncate"
            />
            <button
              onClick={handleCopy}
              className={`px-3 py-2 rounded-xl text-xs font-black transition-all ${
                copied ? 'bg-emerald-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {copied ? '✓ Copié' : 'Copier'}
            </button>
          </div>
          <div className="flex gap-2 mt-2">
            <a href={`/booking/${salon.bookingSlug}`} target="_blank" rel="noopener noreferrer"
              className="text-[11px] text-blue-400 hover:underline">✂ Réservation directe</a>
            {salon.shopSlug && (
              <a href={`/shop/${salon.shopSlug}`} target="_blank" rel="noopener noreferrer"
                className="text-[11px] text-amber-500 hover:underline">🛍 Boutique directe</a>
            )}
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-400 mb-3">
            Votre salon n'a pas encore de lien public.
          </p>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="px-4 py-2.5 bg-blue-600 text-white rounded-xl text-xs font-black hover:bg-blue-700 transition-all disabled:opacity-60 flex items-center gap-2"
          >
            {generating ? 'Génération…' : '🔗 Générer mon lien'}
          </button>
        </>
      )}
    </div>
  );
}

export default AdminSalonDashboard;